// Customer-wise Items & Pricing helper.
//
// When the `sales_customer_pricing` module is enabled, each customer can carry
// a private item list with override prices. This helper centralises:
//   - loading a customer's pricing snapshot (one Firestore round-trip)
//   - filtering an items array when the customer is flagged exclusive
//   - applying the override price after an item-pick
//
// Storage:
//   companies/{companyId}/customerItemPricing/{customerId}__{itemId}
//   { customerId, itemId, price, createdAt, updatedAt }
//
// The "exclusive" flag (restrict this customer to listed items only) lives
// on the customer doc itself: `customer.pricingExclusive: true`. Keeping it
// there avoids a second query and makes the customer master self-contained.

import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

export const EMPTY_PRICING = Object.freeze({
  exclusive: false,
  priceMap: {},        // { itemId: numericPrice }
  allowedItemIds: null // null = no restriction. Set instance when exclusive.
});

/**
 * Load pricing for a single customer.
 * Returns EMPTY_PRICING if the module is off, the customerId is empty, or
 * the customer has no override docs and exclusive is not set.
 *
 * Callers should prefer `loadIfEnabled()` which performs the module check.
 */
export const loadCustomerPricing = async (companyId, customerId) => {
  if (!companyId || !customerId) return { ...EMPTY_PRICING };

  // Pull the customer doc + override list in parallel.
  const [custSnap, pricingSnap] = await Promise.all([
    getDoc(doc(db, 'companies', companyId, 'customers', customerId)),
    getDocs(query(
      collection(db, 'companies', companyId, 'customerItemPricing'),
      where('customerId', '==', customerId)
    ))
  ]);

  const exclusive = custSnap.exists() && custSnap.data().pricingExclusive === true;
  const priceMap = {};
  pricingSnap.docs.forEach(d => {
    const data = d.data();
    if (data?.itemId && Number.isFinite(Number(data.price))) {
      priceMap[data.itemId] = Number(data.price);
    }
  });

  // Only build the allow-list when actually restricting.
  const allowedItemIds = exclusive ? new Set(Object.keys(priceMap)) : null;
  return { exclusive, priceMap, allowedItemIds };
};

/**
 * Module-aware loader. Returns EMPTY_PRICING when the module is off so
 * callers don't have to branch on `isModuleEnabled` themselves.
 */
export const loadIfEnabled = async (companyId, customerId, isModuleEnabled) => {
  if (typeof isModuleEnabled !== 'function') return { ...EMPTY_PRICING };
  if (!isModuleEnabled('sales_customer_pricing')) return { ...EMPTY_PRICING };
  return loadCustomerPricing(companyId, customerId);
};

/**
 * Filter an items array using a pricing snapshot.
 * - When pricing is empty or non-exclusive, returns `items` unchanged.
 * - When exclusive but the allow-list is empty, returns [] — the picker will
 *   render "No items found" which is the correct outcome (admin must add
 *   items to the customer's list before this customer can buy anything).
 */
export const filterItemsByPricing = (items, pricing) => {
  if (!pricing || !pricing.exclusive || !pricing.allowedItemIds) return items;
  return items.filter(i => pricing.allowedItemIds.has(i.id));
};

/**
 * Returns the override price for an item, or `null` when there isn't one.
 * Callers should fall back to their normal pricing (item.salePrice / per-unit
 * price) when this returns null.
 */
export const getOverridePrice = (pricing, itemId) => {
  if (!pricing || !pricing.priceMap || !itemId) return null;
  const v = pricing.priceMap[itemId];
  return Number.isFinite(Number(v)) ? Number(v) : null;
};

/**
 * Build the deterministic doc id used in `customerItemPricing`.
 * Keeping it deterministic means saving an override is an idempotent upsert
 * and deleting one is a single-doc operation.
 */
export const pricingDocId = (customerId, itemId) => `${customerId}__${itemId}`;
